import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Play, UserPlus, LogIn, Star, Trophy, Zap, Target, Crown, Sparkles, ChevronRight, Calendar, ArrowRight, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero image from generation
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/a390495f-3457-4415-8087-a0d4ddeb97ab/images/ab7650ab5e1519dbbafa6e6aa7969300ed103d6e130c6e3c6bba3e6d407b274c.png";

// Game features
const FEATURES = [
  {
    icon: Target,
    title: "200+ Levels",
    description: "Journey through 21 unique worlds with increasing challenges",
    color: "from-green-500 to-emerald-600"
  },
  {
    icon: Crown,
    title: "Epic Boss Battles",
    description: "Face powerful bosses with unique abilities and attacks",
    color: "from-purple-500 to-indigo-600"
  },
  {
    icon: Zap,
    title: "Special Power-ups",
    description: "Unlock bombs, rockets, and color blasters to crush more fruits",
    color: "from-yellow-500 to-orange-600"
  },
  {
    icon: Trophy,
    title: "Compete & Climb",
    description: "Rise through the leaderboards and earn exclusive rewards",
    color: "from-blue-500 to-cyan-600"
  },
  {
    icon: Star,
    title: "Daily Rewards",
    description: "Log in daily for bonuses, spins, and special items",
    color: "from-pink-500 to-rose-600"
  },
  {
    icon: Sparkles,
    title: "Battle Pass",
    description: "Unlock exclusive skins, frames, and cosmetics each season",
    color: "from-amber-500 to-yellow-600"
  }
];

// How to play guidelines
const GUIDELINES = [
  {
    step: 1,
    title: "Match 3 or More",
    description: "Swap adjacent fruits to create matches of 3 or more identical fruits in a row or column."
  },
  {
    step: 2,
    title: "Create Combos",
    description: "Match 4 fruits for a striped bomb, 5 in an L/T shape for a wrapped bomb, or 5 in a row for a disco ball!"
  },
  {
    step: 3,
    title: "Complete Objectives",
    description: "Each level has unique goals - collect fruits, break obstacles, defeat bosses, or reach target scores."
  },
  {
    step: 4,
    title: "Use Power-ups Wisely",
    description: "Combine special fruits for massive explosions. Save your boosters for tough levels!"
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // User is logged in, redirect to game
      navigate("/map");
    }
  }, [navigate]);

  // Fetch latest news
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/news`);
        if (response.ok) {
          const data = await response.json();
          setNews(data.slice(0, 3)); // Show only 3 latest news
        }
      } catch (error) {
        console.error("Failed to fetch news:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🍓</span>
              <span className="text-xl font-bold bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
                Fruit Crush
              </span>
            </div>
            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-1">
              {[
                { label: 'Features', href: '#features' },
                { label: 'How to Play', href: '#how-to-play' },
                { label: 'News', href: '#latest-news' },
                { label: 'FAQ', href: '/faq' },
              ].map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    if (link.href.startsWith('#')) {
                      e.preventDefault();
                      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-3 py-2 text-sm text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => navigate("/login")}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
              <Button 
                className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white"
                onClick={() => navigate("/register")}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Register
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute text-4xl animate-float-random opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${10 + Math.random() * 10}s`
              }}
            >
              {['🍓', '🍊', '🍇', '🍎', '🍋', '🍒'][i % 6]}
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Match, Crush, <br />
                <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                  Conquer!
                </span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg mx-auto lg:mx-0">
                Embark on a fruity adventure through 21 magical worlds. Match colorful fruits, 
                defeat epic bosses, and climb the leaderboards in this addictive puzzle game!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg px-8 py-6 shadow-lg shadow-green-500/30"
                  onClick={() => navigate("/register")}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Play Now - It's Free!
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6"
                  onClick={() => navigate("/login")}
                >
                  Already have an account?
                </Button>
              </div>
              
              {/* Stats */}
              <div className="flex gap-8 mt-10 justify-center lg:justify-start">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">200+</p>
                  <p className="text-slate-400 text-sm">Levels</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">21</p>
                  <p className="text-slate-400 text-sm">Worlds</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">10K+</p>
                  <p className="text-slate-400 text-sm">Players</p>
                </div>
              </div>
            </div>

            {/* Right - Hero image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-3xl blur-3xl"></div>
              <img 
                src={HERO_IMAGE} 
                alt="Fruit Crush Game" 
                className="relative w-full max-w-lg mx-auto rounded-3xl shadow-2xl shadow-purple-500/30 border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Play Fruit Crush?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Discover what makes our game special - from challenging puzzles to epic rewards
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <Card 
                key={index}
                className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section id="latest-news" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                Latest News
              </h2>
              <p className="text-slate-400">Stay updated with game announcements and events</p>
            </div>
          </div>

          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="bg-slate-800/50 border-slate-700/50 animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-slate-700 rounded w-1/4 mb-4"></div>
                    <div className="h-6 bg-slate-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-slate-700 rounded w-full mb-1"></div>
                    <div className="h-4 bg-slate-700 rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : news.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item) => (
                <Card 
                  key={item.id}
                  className="bg-slate-800/50 border-slate-700/50 hover:border-purple-500/50 transition-all duration-300 group cursor-pointer"
                  onClick={() => setSelectedNews(item)}
                  data-testid={`news-card-${item.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                      <Calendar className="w-4 h-4" />
                      {formatDate(item.created_at)}
                      {item.category && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.category === 'update' ? 'bg-blue-500/20 text-blue-400' :
                          item.category === 'event' ? 'bg-green-500/20 text-green-400' :
                          item.category === 'announcement' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {item.category}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-sm line-clamp-3">{item.content}</p>
                    <div className="flex items-center gap-1 text-purple-400 text-sm mt-4 group-hover:gap-2 transition-all">
                      Read more <ChevronRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No news yet. Check back soon for updates!</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* How to Play Section */}
      <section id="how-to-play" className="py-20 bg-gradient-to-b from-purple-950/50 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How to Play
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Master these basics and you'll be crushing fruits like a pro in no time!
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {GUIDELINES.map((guide) => (
              <div key={guide.step} className="relative">
                <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {guide.step}
                </div>
                <Card className="bg-slate-800/50 border-slate-700/50 pt-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-2">{guide.title}</h3>
                    <p className="text-slate-400 text-sm">{guide.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white text-lg px-10 py-6 shadow-lg shadow-pink-500/30"
              onClick={() => navigate("/register")}
            >
              Start Playing Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🍓</span>
                <span className="text-lg font-bold text-white">Fruit Crush</span>
              </div>
              <p className="text-slate-400 text-sm">
                The ultimate match-3 puzzle adventure. Match fruits, defeat bosses, and become the champion!
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="/login" className="text-slate-400 hover:text-white text-sm transition-colors">Login</a></li>
                <li><a href="/register" className="text-slate-400 hover:text-white text-sm transition-colors">Register</a></li>
                <li><a href="/#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</a></li>
                <li><a href="/#how-to-play" className="text-slate-400 hover:text-white text-sm transition-colors">How to Play</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</a></li>
                <li><a href="/cookies" className="text-slate-400 hover:text-white text-sm transition-colors">Cookie Policy</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="/faq" className="text-slate-400 hover:text-white text-sm transition-colors">FAQ</a></li>
                <li><a href="/help" className="text-slate-400 hover:text-white text-sm transition-colors">Help Center</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Fruit Crush. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-slate-500 text-xs">Made with 🍓 by Fruit Crush Team</span>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>{`
        @keyframes float-random {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(5deg); }
          50% { transform: translateY(-10px) rotate(-5deg); }
          75% { transform: translateY(-30px) rotate(3deg); }
        }
        .animate-float-random {
          animation: float-random 15s ease-in-out infinite;
        }
      `}</style>

      {/* News Detail Modal */}
      {selectedNews && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setSelectedNews(null)}
          data-testid="news-modal-backdrop"
        >
          <Card 
            className="bg-slate-900 border-slate-700 w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {/* Header */}
              <div className="p-6 border-b border-slate-700">
                <button 
                  onClick={() => setSelectedNews(null)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-800 transition-colors"
                  data-testid="news-modal-close"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
                
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedNews.created_at)}
                  {selectedNews.category && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedNews.category === 'update' ? 'bg-blue-500/20 text-blue-400' :
                      selectedNews.category === 'event' ? 'bg-green-500/20 text-green-400' :
                      selectedNews.category === 'announcement' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {selectedNews.category}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white pr-8">{selectedNews.title}</h2>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{selectedNews.content}</p>
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-slate-700 flex justify-end">
                <Button 
                  onClick={() => setSelectedNews(null)}
                  variant="outline"
                  className="border-slate-600 hover:bg-slate-800"
                >
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
